import React from "react"
import { Card, Tag, Typography, Space } from "antd"
import { CheckCircleOutlined, CloseCircleOutlined } from "@ant-design/icons"
import { MatchType, PatternMatchDetails } from "./types"

const { Text } = Typography

export interface PatternMatchProps {
  pattern: string
  matchType: MatchType
  testString: string
  isMatch: boolean
  matchDetails?: PatternMatchDetails
}

export const PatternMatch: React.FC<PatternMatchProps> = ({
  pattern,
  matchType,
  testString,
  isMatch,
  matchDetails,
}) => {
  const getMatchTypeColor = (type: MatchType) => {
    switch (type) {
    case MatchType.EXACT:
      return "blue"
    case MatchType.PARTIAL:
      return "green"
    case MatchType.REGEX:
      return "orange"
    default:
      return "default"
    }
  }

  const highlightMatch = (text: string, matched: string, index: number) => {
    if (!matched || index === -1) return text

    const before = text.substring(0, index)
    const match = text.substring(index, index + matched.length)
    const after = text.substring(index + matched.length)

    return (
      <>
        {before}
        <Text mark strong style={{ backgroundColor: "#fff2e6", color: "#d46b08" }}>
          {match}
        </Text>
        {after}
      </>
    )
  }

  return (
    <Card
      size="small"
      style={{
        marginBottom: 8,
        borderColor: isMatch ? "#52c41a" : "#d9d9d9",
      }}
    >
      <Space direction="vertical" size="small" style={{ width: "100%" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Space>
            {isMatch ? (
              <CheckCircleOutlined style={{ color: "#52c41a" }} />
            ) : (
              <CloseCircleOutlined style={{ color: "#8c8c8c" }} />
            )}
            <Text code style={{ fontSize: 12 }}>
              {pattern}
            </Text>
            <Tag color={getMatchTypeColor(matchType)}>
              {matchType}
            </Tag>
          </Space>
          <Text
            type={isMatch ? "success" : "secondary"}
            strong={isMatch}
            style={{ fontSize: 12 }}
          >
            {isMatch ? "MATCH" : "NO MATCH"}
          </Text>
        </div>

        {isMatch && matchDetails && (
          <div
            style={{
              padding: 8,
              backgroundColor: "#f6ffed",
              borderRadius: 4,
              border: "1px solid #b7eb8f",
              fontSize: 12,
            }}
          >
            <Text type="secondary">Matched text: </Text>
            <Text code>
              {highlightMatch(
                testString,
                matchDetails.matchedText || "",
                matchDetails.index || 0,
              )}
            </Text>
            {matchDetails.groups && matchDetails.groups.length > 0 && (
              <div style={{ marginTop: 4 }}>
                <Text type="secondary">Groups: </Text>
                {matchDetails.groups.map((group, index) => (
                  <Tag key={index} color="cyan">
                    {group}
                  </Tag>
                ))}
              </div>
            )}
          </div>
        )}
      </Space>
    </Card>
  )
}
